import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  matchRoutes,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Space } from '@arco-design/web-react';
import {
  Layout,
  Menu,
  Message,
  Popover,
  Spin,
  Watermark,
} from '@arco-design/web-react';
import cs from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import {
  IconMenuFold,
  IconMenuUnfold,
  // IconCaretRight,
  // IconCaretDown,
  // IconDoubleRight,
  IconRight,
} from '@arco-design/web-react/icon';
import Navbar from './components/NavBar';
import Footer from './components/Footer';
import LogoImg from '@/assets/logo.png';
import { IRoute } from '@/router/typings';
import getUrlParams from './utils/getUrlParams';
import { useStore } from './store';
import styles from './style/layout.module.less';
import { transRoutePathToKey, useAuthRoute } from '@/router';
import { useMemoizedFn } from 'ahooks';
import ProgressBar from '@/utils/progressBar';
import settings from './settings.json';
import { checkLogin, redirectToLogin } from '@/utils/auth';
import { getMobileTail } from '@/utils/tools';
import { useOrg } from '@/utils/hooks/useOrg';
import { Helmet } from 'react-helmet';
import TabBar from '@/components/TabBar';
import TodoList from '@/components/TodoList';
import { TabItem } from '@/store/modules/tab';
import TableEllipsis from './components/TableEllipsis';
import {
  navigateWithTabPolicy,
  pickTabOpenTitle,
  resolveMatchedRouteTabTitle,
} from '@/utils/tabPolicy';
import { buildTabRouteCacheKey, isOrgRootPathname } from '@/router/routeMeta';
import { TabKeepAliveShell } from '@/components/layout/TabKeepAliveShell';
import AssistantChat from '@/components/AssistantChat';
import { allowsLayoutScrollStateCache } from '@/utils/tabStateCachePolicy';
import {
  isTabFeatureEnabled,
  loadTabFeatureEnabled,
  saveTabFeatureEnabled,
} from '@/utils/tabFeatureSwitch';
import {
  dispatchLayoutContentResize,
  getCollapsedAfterTodoPanelClose,
  getCollapsedAfterTodoPanelOpen,
  TODO_PANEL_TRANSITION_DURATION,
  TODO_PANEL_WIDTH,
} from '@/utils/todoPanelLayout';
import { useVisualTrackingLayoutInit } from '@/utils/business/visualTracking/useVisualTrackingLayoutInit';
import {
  isCurrentWindowFramed,
  resolveVisualTrackingPreviewMode,
  shouldHideRouteInVisualTrackingPreview,
} from '@/utils/business/visualTracking/previewMode';
import { TrackingClientType } from '@/constants/business/tracking';
const MenuItem = Menu.Item;
const SubMenu = Menu.SubMenu;

const Sider = Layout.Sider;
const Content = Layout.Content;

const safeDecodeURIComponent = (value: string) => {
  let decodedValue = value;

  for (let i = 0; i < 3; i += 1) {
    try {
      const nextValue = decodeURIComponent(decodedValue);
      if (nextValue === decodedValue) {
        break;
      }
      decodedValue = nextValue;
    } catch (error) {
      break;
    }
  }

  return decodedValue;
};

type LayoutTabBarProps = {
  onSwitch: (tab: TabItem) => void;
  onClose: (tab: TabItem) => void;
  onCloseOthers: (tab: TabItem) => void;
  onCloseRight: (tab: TabItem) => void;
  onReorder: (activeKey: string, overKey: string) => void;
};

/** 仅订阅 tabs/activeKey，避免 restoreVersion 等变更牵动整页 layout */
const LayoutTabBar = observer(function LayoutTabBar({
  onSwitch,
  onClose,
  onCloseOthers,
  onCloseRight,
  onReorder,
}: LayoutTabBarProps) {
  const tabStore = useStore('tab');
  return (
    <TabBar
      tabs={tabStore.tabs}
      activeKey={tabStore.activeKey}
      onSwitch={onSwitch}
      onClose={onClose}
      onCloseOthers={onCloseOthers}
      onCloseRight={onCloseRight}
      onReorder={onReorder}
    />
  );
});

const PageLayout = () => {
  const urlParams = getUrlParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isVisualTrackingPreview = resolveVisualTrackingPreviewMode({
    search: location.search,
    isFramed: isCurrentWindowFramed(),
    storage: typeof window === 'undefined' ? null : window.sessionStorage,
  });
  const isOfferConfirmPage = location.pathname.includes('/offer-confirm');
  const isTrackingWorkbenchPage = /\/tracking\/?$/.test(location.pathname);
  const isApprovalTemplateDetailPage =
    /\/system\/approval-template\/detail\//.test(location.pathname);
  const {
    userLoading,
    queryUserInfo,
    member,
    mobileTail,
    curOrganization,
    changeOrganization,
    organizationList,
  } = useStore('user');
  const tabStore = useStore('tab');
  const { queryCommonConfig, commonConfig } = useStore('systemConfig');
  const pathname = location.pathname;
  const { orgId } = useOrg();
  /** dpMenuRoutes.path 为去掉首 `/` 的站内路径（如 system/leave-plan），须拼成绝对路径再 navigate，否则被 RR 当成相对路径从当前页解析，易落到 system/prole 并被 UserRole 重定向到 people */
  const menuPathToOrgAbsolute = useMemoizedFn((menuPath: string) => {
    const normalized = menuPath.startsWith('/') ? menuPath : `/${menuPath}`;
    return `/org/${orgId}${normalized}`;
  });
  const currentComponent = transRoutePathToKey(pathname);
  const {
    permissionRoutes: routes,
    defaultRoutePath: defaultRoute,
    dpMenuRoutes,
  } = useAuthRoute();
  const defaultRouteKey = currentComponent || defaultRoute;
  const defaultSelectedKeys = [transRoutePathToKey(defaultRouteKey)];

  const paths = defaultRouteKey.split('/');
  const defaultOpenKeys = paths.slice(0, paths.length - 1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [breadcrumb, setBreadCrumb] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [todoPanelVisible, setTodoPanelVisible] = useState<boolean>(false);
  const [todoPanelMounted, setTodoPanelMounted] = useState<boolean>(false);
  const [isRecruitmentCandidate, setIsRecruitmentCandidate] =
    useState<boolean>(false);
  const [orgPopoverVisible, setOrgPopoverVisible] = useState<boolean>(false);
  const [tabFeatureEnabled, setTabFeatureEnabledState] = useState<boolean>(
    isTabFeatureEnabled()
  );
  const allowTabFeature = !isVisualTrackingPreview && tabFeatureEnabled;
  const [selectedKeys, setSelectedKeys] =
    useState<string[]>(defaultSelectedKeys);

  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys);
  const contentWrapperRef = useRef<HTMLDivElement | null>(null);
  const collapsedBeforeTodoOpenRef = useRef<boolean>(false);
  const todoPanelVisibleRef = useRef<boolean>(false);
  const todoPanelCloseTimerRef = useRef<number>();
  const todoPanelOpenFrameRef = useRef<number>();
  const lastRouteFullPathRef = useRef('');
  const routeMap = useRef<Map<string, string[]>>(new Map());
  const menuMap = useRef<
    Map<string, { menuItem?: boolean; subMenu?: boolean }>
  >(new Map());

  useVisualTrackingLayoutInit({
    isVisualTrackingPreview,
    orgId: orgId || '',
    accountId: member?.memberId,
    clientType: TrackingClientType.PC,
  });

  const curRoute = useMemo(() => {
    // return route.path;
    const key = transRoutePathToKey(pathname);
    return dpMenuRoutes.find((r) => r.path === key);
  }, [dpMenuRoutes, pathname]);

  todoPanelVisibleRef.current = todoPanelVisible;

  const resolveTabTitle = useMemoizedFn(
    (targetPath: string, targetSearch = '') => {
      const routeKey = transRoutePathToKey(targetPath);
      /** 人员管理 / 角色管理共用一条标签，标题固定为菜单「人员角色」 */
      if (
        routeKey === 'system/prole/people' ||
        routeKey === 'system/prole/role'
      ) {
        return '人员角色';
      }
      const menuRoute = dpMenuRoutes.find((route) => route.path === routeKey);
      const params = new URLSearchParams(targetSearch.replace(/^\?/, ''));
      const tabTitle = params.get('tabTitle') || '';
      if (tabTitle) {
        return safeDecodeURIComponent(tabTitle);
      }
      const dynamicName =
        params.get('title') ||
        params.get('name') ||
        params.get('candidateName') ||
        params.get('userName') ||
        '';
      const decodedDynamicName = safeDecodeURIComponent(dynamicName);
      const matches = matchRoutes(routes, {
        pathname: targetPath,
      });
      const deepMatch = matches?.[matches.length - 1]?.route as
        | IRoute
        | undefined;
      return resolveMatchedRouteTabTitle({
        routeKey,
        menuRouteName: menuRoute?.name,
        matchedRouteName: deepMatch?.name,
        dynamicName: decodedDynamicName,
        hasRouteMatch: Boolean(matches?.length),
      });
    }
  );

  const hasExplicitTabTitle = (targetSearch = '') => {
    const params = new URLSearchParams(targetSearch.replace(/^\?/, ''));
    return Boolean(params.get('tabTitle'));
  };

  const hasDynamicTabTitle = (targetSearch = '') => {
    const params = new URLSearchParams(targetSearch.replace(/^\?/, ''));
    return Boolean(
      params.get('tabTitle') ||
        params.get('title') ||
        params.get('name') ||
        params.get('candidateName') ||
        params.get('userName')
    );
  };

  const syncTabTitles = useMemoizedFn(() => {
    tabStore.tabs.forEach((tab) => {
      const nextTitle = resolveTabTitle(tab.path, tab.search);
      tabStore.updateTabTitle(tab.key, nextTitle);
    });
  });

  useEffect(() => {
    const matchs = matchRoutes(routes, location);
    if (matchs) {
      const deepMatch = matchs?.[matchs?.length - 1];
      if (deepMatch?.route?.keepCollapsed) {
        setCollapsed(true);
      }
    }
  }, [curRoute, routes, location]);

  const navbarHeight = 52;
  const menuWidth = collapsed ? 48 : settings?.menuWidth;
  const menuWidthValue =
    typeof menuWidth === 'number' ? `${menuWidth}px` : menuWidth;

  const showNavbar = settings?.navbar && urlParams.navbar !== false;
  const showMenu =
    settings?.menu && urlParams.menu !== false && !isTrackingWorkbenchPage;
  const showFooter = settings?.footer && urlParams.footer !== false;

  useEffect(() => {
    const init = async () => {
      await queryUserInfo();
      queryCommonConfig();
    };
    if (checkLogin()) {
      init();
    } else if (
      window.location.pathname.replace(/\//g, '') !== 'login' &&
      !isOfferConfirmPage
    ) {
      redirectToLogin();
    }
  }, []);

  useEffect(() => {
    if (!member?.memberId) return;
    let ignore = false;
    loadTabFeatureEnabled().then((enabled) => {
      if (ignore) return;
      setTabFeatureEnabledState(enabled);
      if (!enabled) {
        tabStore.clearAll(member.memberId);
        return;
      }
      tabStore.bindStorageUser(member.memberId);
    });
    return () => {
      ignore = true;
    };
  }, [member?.memberId, tabStore]);

  function renderRoutes() {
    routeMap.current.clear();
    return function travel(
      _routes: IRoute[],
      level: number,
      parentNames: string[] = []
    ) {
      return _routes.map((route) => {
        const { breadcrumb = true, ignore } = route;
        const hideInTrackingPreview = shouldHideRouteInVisualTrackingPreview(
          route.path,
          isVisualTrackingPreview
        );
        const routeKey = transRoutePathToKey(route.path);
        const IconCom = route.icon || <div className={'hr-menu-icon-empty'} />;
        const titleDom = (
          <>
            {IconCom} {route.name}
          </>
        );
        routeMap.current.set(
          routeKey,
          breadcrumb ? [...parentNames, route.name] : []
        );

        const visibleChildren = (route.children || []).filter((child) => {
          const { ignore, breadcrumb = true } = child;
          if (ignore || route.ignore) {
            routeMap.current.set(
              routeKey,
              breadcrumb ? [...parentNames, route.name, child.name] : []
            );
          }

          return !ignore;
        });

        if (ignore || hideInTrackingPreview) {
          return '';
        }
        if (visibleChildren.length) {
          menuMap.current.set(routeKey, { subMenu: true });
          return (
            <SubMenu key={routeKey} title={titleDom}>
              {travel(visibleChildren, level + 1, [...parentNames, route.name])}
            </SubMenu>
          );
        }
        menuMap.current.set(routeKey, { menuItem: true });
        return (
          <MenuItem
            key={routeKey}
            style={{ fontWeight: level === 1 ? 500 : 400 }}
          >
            {titleDom}
          </MenuItem>
        );
      });
    };
  }

  async function onClickMenuItem(key: string) {
    const currentRoute = dpMenuRoutes.find((r) => r.path === key);

    const component = currentRoute?.component;
    if (!orgId) {
      return;
    }
    try {
      ProgressBar.show();
      await component?.preload?.();
      if (currentRoute?.path) {
        const fullPath = menuPathToOrgAbsolute(currentRoute.path);
        if (!allowTabFeature) {
          navigate(fullPath);
          ProgressBar.hide();
          return;
        }
        const tabTarget = navigateWithTabPolicy(fullPath);
        if (tabTarget.shouldOpenNewTab) {
          const resolvedTitle = resolveTabTitle(
            tabTarget.path,
            tabTarget.search
          );
          const result = tabStore.openTab({
            key: tabTarget.tabKey,
            title: pickTabOpenTitle({
              routeMetaTitle: tabTarget.tabTitle,
              resolvedTitle,
              hasDynamicTitle: hasDynamicTabTitle(tabTarget.search),
            }),
            path: tabTarget.path,
            search: tabTarget.search,
            fullPath: tabTarget.fullPath,
            tabMode: tabTarget.tabMode,
            cacheStrategy: tabTarget.cacheStrategy,
            closable: true,
            forceTitle: hasExplicitTabTitle(tabTarget.search),
          });
          if (result.type === 'rejected-by-limit') {
            Message.warning(
              result.reason === 'dirty-instance-blocked'
                ? '存在未保存草稿标签，无法自动回收，请先手动关闭后再新开'
                : '最多打开 15 个标签，请先关闭部分标签'
            );
            ProgressBar.hide();
            return;
          }
        }
        navigate(tabTarget.fullPath);
      }
      ProgressBar.hide();
    } catch (e) {
      console.error(e);
      window.location.reload();
    }
  }

  function toggleCollapse() {
    setCollapsed((collapsed) => !collapsed);
  }

  const handleCloseTodoPanel = useMemoizedFn(() => {
    setTodoPanelVisible(false);
    setCollapsed(
      getCollapsedAfterTodoPanelClose(collapsedBeforeTodoOpenRef.current)
    );
    if (todoPanelCloseTimerRef.current) {
      window.clearTimeout(todoPanelCloseTimerRef.current);
    }
    todoPanelCloseTimerRef.current = window.setTimeout(() => {
      setTodoPanelMounted(false);
    }, TODO_PANEL_TRANSITION_DURATION);
  });

  const handleOpenTodoPanel = useMemoizedFn(() => {
    if (todoPanelVisible) {
      handleCloseTodoPanel();
      return;
    }
    if (todoPanelCloseTimerRef.current) {
      window.clearTimeout(todoPanelCloseTimerRef.current);
    }
    if (todoPanelOpenFrameRef.current) {
      window.cancelAnimationFrame(todoPanelOpenFrameRef.current);
    }
    collapsedBeforeTodoOpenRef.current = collapsed;
    setTodoPanelMounted(true);
    todoPanelOpenFrameRef.current = window.requestAnimationFrame(() => {
      setTodoPanelVisible(true);
    });
    setCollapsed(getCollapsedAfterTodoPanelOpen());
  });

  useEffect(() => {
    return () => {
      if (todoPanelCloseTimerRef.current) {
        window.clearTimeout(todoPanelCloseTimerRef.current);
      }
      if (todoPanelOpenFrameRef.current) {
        window.cancelAnimationFrame(todoPanelOpenFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    dispatchLayoutContentResize();
    const timer = window.setTimeout(
      dispatchLayoutContentResize,
      TODO_PANEL_TRANSITION_DURATION
    );
    return () => window.clearTimeout(timer);
  }, [todoPanelMounted, todoPanelVisible]);

  useEffect(() => {
    const timer = window.setTimeout(dispatchLayoutContentResize, 220);
    return () => window.clearTimeout(timer);
  }, [collapsed]);

  const paddingLeft = showMenu ? { paddingLeft: menuWidth } : {};
  const paddingTop = showNavbar ? { paddingTop: navbarHeight } : {};
  const paddingStyle = { ...paddingLeft, ...paddingTop };
  const navbarStyle =
    showMenu && menuWidthValue
      ? {
          left: menuWidthValue,
          width: `calc(100% - ${menuWidthValue})`,
        }
      : {};

  const updateMenuStatus = useMemoizedFn(() => {
    const curRouteKey = transRoutePathToKey(pathname);
    const pathKeys = curRouteKey.split('/');
    const newSelectedKeys: string[] = [];
    const newOpenKeys: string[] = [...openKeys];
    while (pathKeys.length > 0) {
      const menuKey = pathKeys.join('/');
      const menuType = menuMap.current.get(menuKey);
      if (menuType && menuType.menuItem) {
        newSelectedKeys.push(menuKey);
      }
      if (menuType && menuType.subMenu && !openKeys.includes(menuKey)) {
        newOpenKeys.push(menuKey);
      }
      pathKeys.pop();
    }
    setSelectedKeys(newSelectedKeys);
    setOpenKeys(newOpenKeys);
  });

  const MenuList = renderRoutes()(routes, 1);
  useEffect(() => {
    /** route和path更新时，更新menu状态 */
    const routeConfig = routeMap.current.get(transRoutePathToKey(pathname));
    setBreadCrumb(routeConfig || []);
    if (pathname.includes('recruitment/candidate')) {
      setIsRecruitmentCandidate(true);
    } else {
      setIsRecruitmentCandidate(false);
    }

    Promise.resolve().then(() => {
      updateMenuStatus();
      if (!todoPanelVisibleRef.current) {
        setCollapsed(false);
      }
    });
  }, [pathname, routes]);

  useEffect(() => {
    if (!allowTabFeature) {
      return;
    }

    const syncCurrentRouteTab = () => {
      if (isOrgRootPathname(location.pathname)) {
        return;
      }
      const routeKey = transRoutePathToKey(location.pathname);
      if (!routeKey) {
        return;
      }
      const tabTarget = navigateWithTabPolicy(
        `${location.pathname}${location.search}`
      );
      if (tabTarget.tabKey === '/') {
        return;
      }
      const shouldResetDynamicTitle =
        tabTarget.tabMode === 'reuse' && !hasDynamicTabTitle(tabTarget.search);
      const resolvedTitle = resolveTabTitle(tabTarget.path, tabTarget.search);
      const tabTitle = pickTabOpenTitle({
        routeMetaTitle: tabTarget.tabTitle,
        resolvedTitle,
        hasDynamicTitle: hasDynamicTabTitle(tabTarget.search),
      });
      const result = tabStore.openTab({
        key: tabTarget.tabKey,
        title: tabTitle,
        path: tabTarget.path,
        search: tabTarget.search,
        fullPath: tabTarget.fullPath,
        tabMode: tabTarget.tabMode,
        cacheStrategy: tabTarget.cacheStrategy,
        closable: true,
        forceTitle: hasExplicitTabTitle(tabTarget.search),
        resetDynamicTitle: shouldResetDynamicTitle,
      });
      if (
        result.type !== 'rejected-by-limit' &&
        shouldResetDynamicTitle &&
        tabTitle
      ) {
        tabStore.updateTabTitle(tabTarget.tabKey, tabTitle);
      }
      if (result.type === 'rejected-by-limit') {
        Message.warning(
          result.reason === 'dirty-instance-blocked'
            ? '存在未保存草稿标签，无法自动回收，请先手动关闭后再新开'
            : '最多打开 15 个标签，请先关闭部分标签'
        );
      }
    };

    syncCurrentRouteTab();

    const disposeRestoreReaction = reaction(
      () => tabStore.restoreVersion,
      syncCurrentRouteTab
    );

    return disposeRestoreReaction;
  }, [
    location.pathname,
    location.search,
    resolveTabTitle,
    allowTabFeature,
    tabStore,
  ]);

  useEffect(() => {
    const currentFullPath = `${location.pathname}${location.search}`;
    const currentRouteCacheKey = buildTabRouteCacheKey(
      location.pathname,
      location.search
    );
    if (!contentWrapperRef.current) return;

    if (
      lastRouteFullPathRef.current &&
      lastRouteFullPathRef.current !== currentFullPath
    ) {
      const lastRouteUrl = new URL(
        lastRouteFullPathRef.current,
        window.location.origin
      );
      const lastRouteCacheKey = buildTabRouteCacheKey(
        lastRouteUrl.pathname,
        lastRouteUrl.search
      );
      if (allowsLayoutScrollStateCache(lastRouteFullPathRef.current)) {
        const prevState =
          tabStore.getTabStateCache<Record<string, unknown>>(
            lastRouteCacheKey
          ) || {};
        tabStore.setTabStateCache(lastRouteCacheKey, {
          ...prevState,
          scrollTop: contentWrapperRef.current.scrollTop,
        });
      }
    }

    if (allowsLayoutScrollStateCache(currentFullPath)) {
      const cachedState = tabStore.getTabStateCache<{ scrollTop?: number }>(
        currentRouteCacheKey
      );
      contentWrapperRef.current.scrollTo({
        top: cachedState?.scrollTop ?? 0,
        left: 0,
        behavior: 'auto',
      });
    } else {
      contentWrapperRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
    }
    lastRouteFullPathRef.current = currentFullPath;
  }, [location.pathname, location.search, tabStore]);

  useEffect(() => {
    if (!dpMenuRoutes.length && !routes.length) {
      return;
    }
    syncTabTitles();
  }, [dpMenuRoutes, routes, syncTabTitles]);

  const handleSwitchTab = useMemoizedFn((tabKey: string) => {
    const target = tabStore.tabs.find((tab) => tab.key === tabKey);
    if (!target) return;
    tabStore.activateTab(tabKey);
    navigate(target.fullPath);
  });

  const handleCloseTab = useMemoizedFn(
    (tab: { key: string; fullPath: string }) => {
      const isClosingActive = tabStore.activeKey === tab.key;
      const fallback = tabStore.closeTab(tab.key);
      if (isClosingActive && fallback?.fullPath) {
        navigate(fallback.fullPath);
        return;
      }
      if (isClosingActive && !fallback?.fullPath && orgId) {
        navigate(menuPathToOrgAbsolute(defaultRoute || 'home'));
      }
    }
  );

  const handleCloseOthers = useMemoizedFn(
    (tab: { key: string; fullPath: string }) => {
      tabStore.closeOthers(tab.key);
      if (`${location.pathname}${location.search}` !== tab.fullPath) {
        navigate(tab.fullPath);
      }
    }
  );

  const handleEnableTabFeature = useMemoizedFn(() => {
    saveTabFeatureEnabled(true);
    if (member?.memberId) {
      tabStore.bindStorageUser(member.memberId);
    }
    setTabFeatureEnabledState(true);
  });

  const handleDisableTabFeature = useMemoizedFn(() => {
    saveTabFeatureEnabled(false);
    tabStore.clearAll(member?.memberId);
    setTabFeatureEnabledState(false);
  });

  const handleCloseRight = useMemoizedFn(
    (tab: { key: string; fullPath: string }) => {
      const currentPath = `${location.pathname}${location.search}`;
      tabStore.closeRight(tab.key);
      const isCurrentRemoved = !tabStore.tabs.find(
        (t) => t.fullPath === currentPath
      );
      if (isCurrentRemoved) {
        navigate(tab.fullPath);
      }
    }
  );

  const handleTabFeatureChange = useMemoizedFn((enabled: boolean) => {
    if (enabled) {
      handleEnableTabFeature();
      return;
    }
    handleDisableTabFeature();
  });

  const onOrgMenuClick = (orgId: string) => {
    setOrgPopoverVisible(false);
    changeOrganization(orgId, () => {
      navigate(`/org/${orgId}/`);
    });
  };

  const orgDropList = (
    <div className={styles['organization-popover-card']}>
      <Menu
        className={styles['organization-popover-menu']}
        selectedKeys={[`${curOrganization?.id || ''}`]}
        onClickMenuItem={onOrgMenuClick}
      >
        {organizationList.map((item) => (
          <Menu.Item key={`${item.id || ''}`}>
            &nbsp;&nbsp;{item.name}&nbsp;&nbsp;
          </Menu.Item>
        ))}
      </Menu>
    </div>
  );

  if (isOfferConfirmPage) {
    return <Outlet />;
  }

  return (
    <Layout className={styles.layout}>
      <Helmet>
        <title>EHR</title>
        <link rel="shortcut icon" type="image/x-icon" href="/favicon.ico" />
      </Helmet>
      <div
        className={cs(styles['layout-navbar'], {
          [styles['layout-navbar-hidden']]: !showNavbar,
        })}
        data-tracking-chrome="navbar"
        style={navbarStyle}
      >
        <Navbar
          tabFeatureEnabled={tabFeatureEnabled}
          onTabFeatureChange={
            isVisualTrackingPreview ? undefined : handleTabFeatureChange
          }
          todoPanelVisible={todoPanelVisible}
          onTodoPanelOpen={handleOpenTodoPanel}
          tabBar={
            allowTabFeature && !isTrackingWorkbenchPage ? (
              <LayoutTabBar
                onSwitch={(tab) => handleSwitchTab(tab.key)}
                onClose={(tab) => handleCloseTab(tab)}
                onCloseOthers={(tab) => handleCloseOthers(tab)}
                onCloseRight={(tab) => handleCloseRight(tab)}
                onReorder={(activeKey, overKey) =>
                  tabStore.moveTab(activeKey, overKey)
                }
              />
            ) : null
          }
        />
      </div>
      {userLoading ? (
        <Spin className={styles.spin} size={24} />
      ) : (
        <Layout hasSider>
          {showMenu && (
            <Sider
              className={styles['layout-sider']}
              data-tracking-chrome="sider"
              width={menuWidth}
              collapsed={collapsed}
              onCollapse={setCollapsed}
              trigger={null}
              collapsible
              breakpoint="xl"
            >
              <div
                className={cs(styles['sider-logo'], {
                  [styles['sider-logo-collapsed']]: collapsed,
                })}
              >
                <img src={LogoImg} alt="EHR" />
                {curOrganization && !collapsed && (
                  <Popover
                    className={styles['organization-popover']}
                    content={orgDropList}
                    position="right"
                    trigger={'click'}
                    disabled={organizationList?.length <= 1}
                    popupVisible={orgPopoverVisible}
                    onVisibleChange={setOrgPopoverVisible}
                  >
                    <Space
                      size={4}
                      className={styles['sider-logo-organization-name-wrapper']}
                    >
                      <TableEllipsis
                        text={curOrganization?.name || ''}
                        maxWidth={90}
                        style={{
                          color: 'rgb(31, 120, 212)',
                          fontSize: '15px',
                          fontWeight: 500,
                        }}
                      />
                      {organizationList?.length > 1 && (
                        <IconRight
                          className={
                            styles['sider-logo-organization-name-right']
                          }
                        />
                      )}
                    </Space>
                  </Popover>
                )}
              </div>
              <div className={styles['menu-wrapper']}>
                <Menu
                  collapse={collapsed}
                  onClickMenuItem={onClickMenuItem}
                  selectedKeys={selectedKeys}
                  openKeys={openKeys}
                  onClickSubMenu={(_, openKeys) => setOpenKeys(openKeys)}
                >
                  {MenuList}
                </Menu>
              </div>
              <div className={styles['collapse-btn']} onClick={toggleCollapse}>
                {collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
              </div>
            </Sider>
          )}

          <Layout
            className={cs(styles['layout-content'], {
              [styles['layout-content-recruitment']]: isRecruitmentCandidate,
              [styles['layout-content-todo-open']]: todoPanelMounted,
              [styles['layout-content-tracking']]: isTrackingWorkbenchPage,
              [styles['layout-content-approval-template']]:
                isApprovalTemplateDetailPage,
            })}
            style={paddingStyle}
          >
            <Content
              className={styles['layout-content-wrapper']}
              ref={contentWrapperRef}
              data-layout-content-wrapper
            >
              <div className={styles['layout-page-container']}>
                <TabKeepAliveShell />
              </div>
              {commonConfig?.watermark?.enable && member?.name && (
                <Watermark
                  getContainer={() =>
                    document.querySelector('.arco-layout-content')!
                  }
                  zIndex={1}
                  content={member?.name + getMobileTail(mobileTail)}
                  fontStyle={{
                    color: 'rgba(0,0,0,0.04)',
                  }}
                />
              )}
            </Content>

            {todoPanelMounted && (
              <aside
                data-layout-todo-panel-visible={
                  todoPanelVisible ? 'true' : 'false'
                }
                className={cs(styles['layout-todo-panel'], {
                  [styles['layout-todo-panel-visible']]: todoPanelVisible,
                })}
                style={{ width: todoPanelVisible ? TODO_PANEL_WIDTH : 0 }}
              >
                <TodoList
                  onCancel={handleCloseTodoPanel}
                  orgId={orgId || ''}
                  isShowTitle={false}
                  autoFetch={true}
                  showClose={true}
                  closeOnItemClick={false}
                />
              </aside>
            )}

            {showFooter && <Footer />}
          </Layout>
        </Layout>
      )}
      {!isVisualTrackingPreview && <AssistantChat />}
    </Layout>
  );
};

export default observer(PageLayout);
